/**
 * Functional Unit normalization
 *
 * Results are computed as absolute totals per functional unit (FU). For battery
 * work the metric people compare on is **per kWh** (kg CO₂/kWh, $/kWh), which
 * makes cross-chemistry comparison valid. This helper converts the mass-based FU
 * into an energy basis using a configurable energy density.
 *
 *   energy_kWh (per FU) = weight_kg × energy_density_wh_per_kg / 1000
 *   result_per_kWh      = total_per_FU / energy_kWh
 *
 * Basis lives in `project_info.fu_basis` ('energy' | 'mass'), alongside the
 * existing run params (scaling_factor, battery_life_extension). Energy density
 * resolves in order: explicit `project_info.energy_density_wh_per_kg` → the
 * template's `functional_unit.energy_density_wh_per_kg` → a standard
 * chemistry-keyed default (so projects created before this field existed, or
 * whose template lacks it, still get a sensible per-kWh basis).
 *
 * Graceful fallback: when basis is 'energy' but no usable energy density exists
 * (blank/custom projects with an unknown chemistry, recycling/module templates),
 * we fall back to the mass basis and flag it so the UI can surface the assumption.
 */

/**
 * Standard cell-level gravimetric energy densities (Wh/kg) keyed by chemistry.
 * Used as the default when a project doesn't carry an explicit value. These are
 * literature-typical screening defaults; users can override per project.
 */
export const DEFAULT_ENERGY_DENSITY_WH_PER_KG = {
  NMC111: 150,
  NMC333: 150, // alias for NMC111 (1:1:1)
  NMC532: 180,
  NMC622: 200,
  NMC811: 250,
  NCA: 250,
  LFP: 160,
  LMO: 110,
};

/** Resolve the standard default energy density for a project's chemistry. */
export function defaultEnergyDensity(project) {
  const raw = (project && project.project_info && project.project_info.chemistry)
    || (project && project.functional_unit && project.functional_unit.chemistry)
    || '';
  const key = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
  return DEFAULT_ENERGY_DENSITY_WH_PER_KG[key] ?? null;
}

/**
 * @param {object} project
 * @returns {{
 *   perKwh: boolean,        // true when results should be divided by `divisor`
 *   divisor: number,        // kWh per FU (1 when on mass basis)
 *   basis: 'energy'|'mass', // effective basis after fallback
 *   kwh?: number,           // energy per FU in kWh (when perKwh)
 *   whPerKg?: number,       // energy density used (Wh/kg)
 *   weightKg?: number,      // FU mass used in the conversion
 *   fallback?: boolean,     // true when requested 'energy' but fell back to mass
 * }}
 */
export function computeFUNormalizer(project) {
  const pi = (project && project.project_info) || {};
  const fu = (project && project.functional_unit) || {};

  const basis = pi.fu_basis || 'energy';
  if (basis !== 'energy') {
    return { perKwh: false, divisor: 1, basis: 'mass' };
  }

  const whPerKg = pi.energy_density_wh_per_kg ?? fu.energy_density_wh_per_kg ?? defaultEnergyDensity(project);
  const weightKg = fu.weight_kg || 1.0;
  const kwh = whPerKg && weightKg ? (whPerKg * weightKg) / 1000 : 0;

  if (!kwh) {
    // Requested energy basis but no usable density → fall back to mass, flagged.
    return { perKwh: false, divisor: 1, basis: 'mass', fallback: true };
  }

  return { perKwh: true, divisor: kwh, basis: 'energy', kwh, whPerKg, weightKg };
}
